import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Flag, RefreshCw, Search } from "lucide-react";
import {
  HomeworkHistoryAssignment,
  HomeworkHistoryFilter,
} from "../api/contracts";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  ActivityTypePill,
  ActionsColumnHeader,
  BooleanIndicator,
  ClientValuePill,
  HomeworkStatusPill,
  PortalDataTable,
  PortalTableColumn,
  SudsPill,
} from "../components/DataDisplay";
import { ActivityStepsPreview } from "../components/ActivityStepsPreview";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  DeleteHomeworkDialog,
  RowActionMenu,
} from "../components/HomeworkControls";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { ReportHelpTooltip } from "../components/ReportVisuals";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import {
  TableColumnsButton,
  usePortalTablePreferences,
} from "../components/TableColumnPreferences";
import {
  clientHomeworkDetailPath,
  clientPrimaryNavigation,
  clientWorkspacePath,
  homeworkDetailEditPath,
} from "../config/navigation";
import { getActivityTypePillToken } from "../config/palette";
import { downloadCsv, sanitizeCsvFilenamePart } from "../csvExport";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";
import {
  comparePortalDate,
  comparePortalNumber,
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";
import { readTeamClinicId, withTeamClinicScope } from "../utils/teamClinicScope";

const historyFilterOptions: readonly {
  value: HomeworkHistoryFilter;
  label: string;
  ariaLabel?: string;
  description: string;
}[] = [
  {
    value: "completed-last-30-days",
    label: "30 Days",
    ariaLabel: "Completed Last 30 Days",
    description: "Completed assignments from the last 30 days.",
  },
  {
    value: "completed-with-modification",
    label: "Modified",
    ariaLabel: "Completed with Modification",
    description: "Assignments completed with modification.",
  },
  {
    value: "attempted",
    label: "Attempted",
    description: "Assignments recorded as attempted.",
  },
  {
    value: "missed",
    label: "Missed",
    description: "Assignments recorded as missed.",
  },
  {
    value: "completed-all",
    label: "Completed",
    description: "All assignments recorded as completed.",
  },
  {
    value: "all-activities",
    label: "All",
    description: "All assignments including completed, modified, attempted, and missed assignments.",
  },
];

const DEFAULT_HISTORY_FILTER: HomeworkHistoryFilter = "completed-last-30-days";

type HomeworkHistorySortKey =
  | "overdue"
  | "completedAt"
  | "dueDate"
  | "homeworkName"
  | "activityType"
  | "valueSupportedName"
  | "completionStatus"
  | "firstSuds"
  | "secondSuds"
  | "sudsChange"
  | "assignedDate";

function getSudsChange(
  assignment: HomeworkHistoryAssignment,
): number | null {
  const before = assignment.firstSuds;
  const after = assignment.secondSuds;

  return before != null && after != null ? after - before : null;
}

function SudsChange({
  assignment,
}: {
  assignment: HomeworkHistoryAssignment;
}) {
  const sudsChange = getSudsChange(assignment);

  if (sudsChange == null) {
    return <span className="cp-empty-value">–</span>;
  }

  const trend =
    sudsChange < 0 ? "reduction" : sudsChange > 0 ? "increase" : "unchanged";
  const label =
    sudsChange < 0
      ? `SUDS decreased by ${Math.abs(sudsChange)}`
      : sudsChange > 0
        ? `SUDS increased by ${sudsChange}`
        : "No SUDS change";
  const displayValue =
    sudsChange < 0
      ? `↓ ${Math.abs(sudsChange)}`
      : sudsChange > 0
        ? `↑ ${sudsChange}`
        : "0";

  return (
    <span
      className="cp-suds-change"
      data-trend={trend}
      aria-label={label}
    >
      {displayValue}
    </span>
  );
}

function mutationFailure(authorized: boolean, message: string) {
  return new ClinicianPortalApiError(
    message,
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

export function HomeworkHistoryPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const [search, setSearch] = useState("");
  const [activityTypeFilter, setActivityTypeFilter] = useState("");
  const [historyFilter, setHistoryFilter] = useState<HomeworkHistoryFilter>(
    DEFAULT_HISTORY_FILTER,
  );
  const [sortKey, setSortKey] =
    useState<HomeworkHistorySortKey>("completedAt");
  const [sortDirection, setSortDirection] =
    useState<PortalSortDirection>("descending");
  const [deleteTarget, setDeleteTarget] =
    useState<HomeworkHistoryAssignment | null>(null);
  const [openActivityStepsId, setOpenActivityStepsId] = useState<string | null>(
    null,
  );
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            historyFilter,
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            historyFilter,
            signal,
          ),
    [clinicianCode, historyFilter, patientId, workspace.accessScope],
  );
  const state = usePortalData(request);
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.patient,
  );
  const filterMatchesData = data?.historyFilter === historyFilter;
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;
  const isMutating = mutation.state.status === "pending";
  const pendingAction =
  mutation.state.status === "pending" ? mutation.state.action : "";
  const scopedHomeworkDetailPath = (homeworkAssignmentId: string) =>
    workspace.source === "team"
      ? withTeamClinicScope(
          clientWorkspacePath(
            patientId,
            `homework-history/${homeworkAssignmentId}`,
            "team",
          ),
          readTeamClinicId(),
        )
      : clientHomeworkDetailPath(patientId, homeworkAssignmentId, "homework-history");
  const selectedFilter =
    historyFilterOptions.find((option) => option.value === historyFilter) ??
    historyFilterOptions[3];
  const activityTypeOptions = useMemo(() => {
    const activityTypes = Array.from(
      new Set(
        (data?.assignments ?? [])
          .map((assignment) => assignment.activityType.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { id: "all-activity-types", value: "", label: "All Activity Types" },
      ...activityTypes.map((activityType) => ({
        id: activityType,
        value: activityType,
        label: activityType,
      })),
    ];
  }, [data?.assignments]);
  const matchingAssignments = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (data?.assignments ?? []).filter((assignment) => {
      if (
        activityTypeFilter &&
        assignment.activityType.trim() !== activityTypeFilter
      ) {
        return false;
      }
      if (!query) return true;
      return [
        assignment.homeworkName,
        assignment.activityType,
        assignment.valueSupportedName,
        assignment.activitySteps,
        assignment.completionStatus,
        assignment.dueDate ? formatPortalDate(assignment.dueDate) : "",
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [activityTypeFilter, data?.assignments, search]);
  const sortedAssignments = useMemo(
    () =>
      stablePortalSort(matchingAssignments, (left, right) => {
        switch (sortKey) {
          case "overdue":
            return comparePortalNumber(
              left.overdue === undefined ? undefined : Number(left.overdue),
              right.overdue === undefined ? undefined : Number(right.overdue),
              sortDirection,
            );
          case "completedAt":
          case "dueDate":
            return comparePortalDate(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
          case "assignedDate":
            return comparePortalDate(
              left.assignedDate,
              right.assignedDate,
              sortDirection,
            );
          case "firstSuds":
          case "secondSuds":
            return comparePortalNumber(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
          case "sudsChange":
            return comparePortalNumber(
              getSudsChange(left),
              getSudsChange(right),
              sortDirection,
            );
          default:
            return comparePortalText(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
        }
      }),
    [matchingAssignments, sortDirection, sortKey],
  );

  const updateSort = (nextSortKey: HomeworkHistorySortKey): void => {
    setSortDirection((currentDirection) =>
      nextSortKey === sortKey && currentDirection === "ascending"
        ? "descending"
        : "ascending",
    );
    setSortKey(nextSortKey);
    setOpenActivityStepsId(null);
  };

  const sortProps = (key: HomeworkHistorySortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const totalAssignments = data?.assignments.length ?? 0;
  const hasActiveFilters = Boolean(search.trim() || activityTypeFilter);
  const resultCount = hasActiveFilters
    ? `${matchingAssignments.length} of ${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`
    : `${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`;

  useEffect(() => {
    if (
      openActivityStepsId &&
      !matchingAssignments.some(
        (assignment) => assignment.id === openActivityStepsId,
      )
    ) {
      setOpenActivityStepsId(null);
    }
  }, [matchingAssignments, openActivityStepsId]);

  const refreshHomeworkHistory = () => {
    mutation.reset();
    state.refresh();
  };

  const exportHomeworkHistory = () => {
    const accessCode = patient?.accessCode || patientId;
    const exportedAt = new Date().toISOString().slice(0, 10);
    const headers = [
      "Completed",
      "Due",
      "Homework Activity",
      "Status",
      "Activity Type",
      "Before",
      "After",
      "Change",
      "Alert",
      "Routine Time",
      "Activity Steps",
      "Value",
      "Assigned",
      "Conversation URL",
    ];
    downloadCsv({
      filename: `${sanitizeCsvFilenamePart(accessCode)}-homework-history-${historyFilter}-${exportedAt}.csv`,
      headers,
      rows: sortedAssignments.map((assignment) => [
        assignment.completedAt ? formatPortalDate(assignment.completedAt) : "",
        assignment.dueDate ? formatPortalDate(assignment.dueDate) : "",
        assignment.homeworkName,
        assignment.completionStatus,
        assignment.activityType,
        assignment.firstSuds ?? "",
        assignment.secondSuds ?? "",
        getSudsChange(assignment) ?? "",
        assignment.emailAlert ? "Yes" : "No",
        assignment.routineTime,
        assignment.activitySteps,
        assignment.valueSupportedName || "",
        formatPortalDate(assignment.assignedDate),
        assignment.conversationReportUrl,
      ]),
    });
  };

  const changeHistoryFilter = (nextFilter: HomeworkHistoryFilter) => {
    if (nextFilter === historyFilter) return;
    mutation.reset();
    setOpenActivityStepsId(null);
    setActivityTypeFilter("");
    setSortKey(nextFilter === "missed" ? "assignedDate" : "completedAt");
    setSortDirection("descending");
    setHistoryFilter(nextFilter);
  };

  const duplicateHomework = async (homeworkAssignmentId: string) => {
    if (workspace.readOnly) return;
    const result = await mutation.run(
      `duplicate:${homeworkAssignmentId}`,
      async (signal) => {
        const response = await clinicianPortalApi.duplicateHomework(
          clinicianCode,
          patientId,
          homeworkAssignmentId,
          signal,
        );
        if (!response.success || !response.duplicated || !response.homework) {
          throw mutationFailure(
            response.authorized,
            "The homework assignment could not be duplicated.",
          );
        }
        return response;
      },
    );

    if (result) state.refresh();
  };

  const deleteHomework = async () => {
    if (workspace.readOnly || !deleteTarget) return;
    const assignment = deleteTarget;
    const result = await mutation.run(
      `delete:${assignment.id}`,
      async (signal) => {
        const response = await clinicianPortalApi.deleteHomework(
          clinicianCode,
          patientId,
          assignment.id,
          signal,
        );
        if (
          !response.success ||
          !response.deleted ||
          response.homeworkAssignmentId !== assignment.id
        ) {
          throw mutationFailure(
            response.authorized,
            "The homework activity could not be deleted.",
          );
        }
        return response;
      },
    );

    if (result) {
      setDeleteTarget(null);
      state.refresh();
    }
  };

  const completedDateColumn: PortalTableColumn<HomeworkHistoryAssignment> = {
    key: "completedAt",
    header: "Completed",
    initialWidth: 112,
    minWidth: 112,
    ...sortProps("completedAt"),
    cellClassName: "cp-table-cell--date-time",
    render: (assignment) => formatPortalDate(assignment.completedAt),
  };
  const dueDateColumn: PortalTableColumn<HomeworkHistoryAssignment> = {
    key: "dueDate",
    header: "Due",
    headerLabel: "Due Date",
    initialWidth: 112,
    minWidth: 112,
    ...sortProps("dueDate"),
    cellClassName: "cp-table-cell--date-time",
    render: (assignment) => formatPortalDate(assignment.dueDate),
  };
  const assignedDateColumn: PortalTableColumn<HomeworkHistoryAssignment> = {
    key: "assignedDate",
    header: "Assigned",
    initialWidth: 112,
    minWidth: 112,
    ...sortProps("assignedDate"),
    cellClassName: "cp-table-cell--date-time",
    render: (assignment) => formatPortalDate(assignment.assignedDate),
  };
  const editableColumns: readonly PortalTableColumn<HomeworkHistoryAssignment>[] = [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          editPath={homeworkDetailEditPath(
            scopedHomeworkDetailPath(assignment.id),
          )}
          activityName={assignment.homeworkName}
          canEdit={Boolean(assignment.homeworkName)}
          canDuplicate={Boolean(assignment.homeworkName) && !isMutating}
          isDuplicating={pendingAction === `duplicate:${assignment.id}`}
          onDuplicate={() => duplicateHomework(assignment.id)}
          canDelete={!isMutating}
          isDeleting={pendingAction === `delete:${assignment.id}`}
          onDelete={() => {
            mutation.reset();
            setDeleteTarget(assignment);
          }}
        />
      ),
    },
    completedDateColumn,
    dueDateColumn,
    {
      key: "overdue",
      header: "Overdue",
      initialWidth: 100,
      minWidth: 100,
      ...sortProps("overdue"),
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => assignment.overdue === undefined
        ? <span className="cp-empty-value">–</span>
        : <BooleanIndicator value={assignment.overdue} trueTone="danger" />,
    },
    {
      key: "homeworkName",
      header: "Homework Activity",
      initialWidth: 255,
      minWidth: 205,
      ...sortProps("homeworkName"),
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) => (
        <span
          className="cp-homework-name cp-homework-activity-name"
          title={assignment.homeworkName || undefined}
        >
          {assignment.homeworkName || "–"}
        </span>
      ),
    },
    {
      key: "completionStatus",
      header: "Status",
      initialWidth: 112,
      minWidth: 112,
      ...sortProps("completionStatus"),
      render: (assignment) => (
        <HomeworkStatusPill status={assignment.completionStatus} />
      ),
    },
    {
      key: "activityType",
      header: "Activity Type",
      initialWidth: 175,
      minWidth: 140,
      ...sortProps("activityType"),
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) => (
        <ActivityTypePill
          activityType={assignment.activityType}
          title={assignment.activityType}
        />
      ),
    },
    {
      key: "firstSuds",
      header: "Before",
      initialWidth: 72,
      minWidth: 72,
      ...sortProps("firstSuds"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <SudsPill
          value={assignment.firstSuds}
          emptyVariant="circle"
          emptyLabel="No before rating"
        />
      ),
    },
    {
      key: "secondSuds",
      header: "After",
      initialWidth: 72,
      minWidth: 72,
      ...sortProps("secondSuds"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <SudsPill
          value={assignment.secondSuds}
          emptyVariant="circle"
          emptyLabel="No after rating"
        />
      ),
    },
    {
      key: "sudsChange",
      header: "Change",
      initialWidth: 90,
      minWidth: 90,
      ...sortProps("sudsChange"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => <SudsChange assignment={assignment} />,
    },
    {
      key: "emailAlert",
      header: "Alert",
      initialWidth: 68,
      minWidth: 68,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => {
        const alertLabel = assignment.emailAlert
          ? "Elevated SUDS alert sent"
          : "No elevated SUDS alert";
        const alertDefinition = assignment.emailAlert
          ? "Elevated SUDS Alert · The OCD Homework Assistant detected elevated SUDS during this activity and emailed the client’s therapist."
          : "No Elevated SUDS Alert · No elevated SUDS alert was sent for this activity.";

        return (
          <span className="cp-history-alert-tooltip">
            <ReportHelpTooltip
              label={alertLabel}
              ariaLabel={alertLabel}
              definition={alertDefinition}
              trigger={
                <span
                  className="cp-history-alert-indicator"
                  data-active={assignment.emailAlert}
                  aria-hidden="true"
                >
                  <Flag aria-hidden="true" />
                </span>
              }
            />
          </span>
        );
      },
    },
    {
      key: "routineTime",
      header: <span title="Routine Time">Duration</span>,
      headerLabel: "Routine Time",
      initialWidth: 90,
      minWidth: 90,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) =>
        assignment.routineTime.trim() || (
          <span className="cp-empty-value">–</span>
        ),
    },
    {
      key: "activitySteps",
      header: "Activity Steps",
      initialWidth: 330,
      minWidth: 220,
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) =>
        assignment.activitySteps ? (
          <ActivityStepsPreview
            activityName={assignment.homeworkName}
            activitySteps={assignment.activitySteps}
            isOpen={openActivityStepsId === assignment.id}
            onOpenChange={(open) =>
              setOpenActivityStepsId(open ? assignment.id : null)
            }
          />
        ) : (
          <span className="cp-empty-value">–</span>
        ),
    },
    {
      key: "valueSupportedName",
      header: "Value",
      initialWidth: 160,
      minWidth: 160,
      ...sortProps("valueSupportedName"),
      render: (assignment) => (
        <ClientValuePill value={assignment.valueSupportedName} />
      ),
    },
    assignedDateColumn,
    {
      key: "conversation",
      header: "Conversation",
      initialWidth: 130,
      minWidth: 115,
      resizable: false,
      sticky: "right",
      render: (assignment) =>
        assignment.conversationReportUrl ? (
          <a
            className="cp-conversation-link"
            href={assignment.conversationReportUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </a>
        ) : (
          <span className="cp-empty-value">–</span>
        ),
    },
  ];
  const columns = workspace.readOnly
    ? editableColumns.filter((column) => column.key !== "actions")
    : editableColumns;
  const tablePreference = usePortalTablePreferences({
    clinicianCode,
    tableKey: "homework_history",
    columns,
    rules: [
      {
        key: "actions",
        hideable: false,
        movable: false,
        position: "left",
      },
      { key: "homeworkName", hideable: false },
      { key: "overdue", defaultHidden: true },
      {
        key: "conversation",
        movable: false,
        position: "right",
      },
    ],
  });

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Homework History",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="homework-history"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list cp-page-content--compact-client-list cp-homework-history">
        {state.status === "loading" ? (
          <LoadingState label="Loading homework history" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Homework history is not available")} />
        ) : null}
        {available ? (
          <section aria-labelledby="homework-history-heading">
            <div className="cp-content-header cp-content-header--route-intro">
              <div>
                <h2
                  id="homework-history-heading"
                  className="cp-visually-hidden"
                >
                  Homework History
                </h2>
                <p>{selectedFilter.description}</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>

            <div className="cp-list-toolbar cp-list-toolbar--homework cp-history-command-bar">
              <div
                className="cp-history-filters"
                role="group"
                aria-label="Homework history filter"
              >
                {historyFilterOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    aria-label={option.ariaLabel}
                    aria-pressed={historyFilter === option.value}
                    disabled={isMutating}
                    onClick={() => changeHistoryFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="cp-list-toolbar__filters cp-list-toolbar__filters--homework">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">
                    Search homework history
                  </span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search homework history"
                    onChange={(event) => {
                      setOpenActivityStepsId(null);
                      setSearch(event.target.value);
                    }}
                  />
                </label>
                <label className="cp-homework-list-filter cp-homework-list-filter--activity-type">
                  <span className="cp-visually-hidden">
                    Filter by Activity Type
                  </span>
                  <SingleSelectPillField
                    id="homework-history-activity-type-filter"
                    value={activityTypeFilter}
                    options={activityTypeOptions}
                    ariaLabel="Filter by Activity Type"
                    placeholder="All Activity Types"
                    searchPlaceholder="Search Activity Types"
                    emptyMessage="No Activity Types found"
                    resolveColor={(option) =>
                      option.value
                        ? getActivityTypePillToken(option.label)
                        : "neutral"
                    }
                    onChange={(nextValue) => {
                      setOpenActivityStepsId(null);
                      setActivityTypeFilter(nextValue);
                    }}
                  />
                </label>
              </div>
              <div className="cp-content-header__actions cp-list-toolbar__actions">
                <TableColumnsButton preference={tablePreference} />
                <button
                  type="button"
                  className="cp-button cp-button--secondary cp-button--icon-only"
                  aria-label={
                    state.status === "refreshing" ? "Refreshing…" : "Refresh"
                  }
                  title={
                    state.status === "refreshing" ? "Refreshing…" : "Refresh"
                  }
                  data-loading={state.status === "refreshing" || undefined}
                  disabled={state.status === "refreshing" || isMutating}
                  onClick={refreshHomeworkHistory}
                >
                  <RefreshCw aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  disabled={
                    state.status === "refreshing" ||
                    isMutating ||
                    !filterMatchesData ||
                    matchingAssignments.length === 0
                  }
                  onClick={exportHomeworkHistory}
                >
                  <Download aria-hidden="true" />
                  Export CSV
                </button>
              </div>
            </div>

            {mutation.state.status === "error" &&
            !mutation.state.action.startsWith("delete:") ? (
              <div
                className="cp-inline-notice cp-inline-notice--error"
                role="alert"
              >
                <div>
                  <strong>Unable to duplicate that homework activity.</strong>
                  <span>Please try again.</span>
                </div>
                <button type="button" onClick={mutation.reset}>
                  Dismiss
                </button>
              </div>
            ) : null}
            {mutation.state.status === "success" ? (
              <div className="cp-inline-notice" role="status">
                {mutation.state.action.startsWith("delete:") ? (
                  "Homework activity deleted."
                ) : (
                  <>
                    <span>
                      The assignment was duplicated to Current Homework.
                    </span>
                    <PortalLink
                      path={
                        clientPrimaryNavigation
                          .find((item) => item.key === "current-homework")
                          ?.path(patientId) ??
                        `/clinician-portal/clients/${encodeURIComponent(
                          patientId,
                        )}/current-homework`
                      }
                      clinicianCode={clinicianCode}
                    >
                      View Current Homework
                    </PortalLink>
                  </>
                )}
              </div>
            ) : null}

            {data && !filterMatchesData ? (
              <LoadingState
                label={`Loading ${selectedFilter.ariaLabel ?? selectedFilter.label}`}
              />
            ) : matchingAssignments.length > 0 ? (
              <PortalDataTable
                caption={`Homework History — ${selectedFilter.ariaLabel ?? selectedFilter.label}`}
                density="compact"
                columns={tablePreference.visibleColumns}
                preferredColumnWidths={tablePreference.columnWidths}
                onColumnWidthsChange={tablePreference.updateColumnWidths}
                records={sortedAssignments}
                getRecordKey={(assignment) => assignment.id}
                getRecordOpenLabel={(assignment) =>
                  `Open ${assignment.homeworkName || "homework assignment"}`
                }
                isRecordOpenable={(assignment) =>
                  Boolean(assignment.homeworkName)
                }
                onRecordOpen={(assignment) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      scopedHomeworkDetailPath(assignment.id),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <div className="cp-homework-table__empty">
                <h3>
                  {data?.assignments.length
                    ? "No homework history matches your filters"
                    : "No matching homework history"}
                </h3>
                <p>
                  {data?.assignments.length
                    ? "Try a different search or filter."
                    : `No assignments match ${(
                        selectedFilter.ariaLabel ?? selectedFilter.label
                      ).toLowerCase()}.`}
                </p>
              </div>
            )}
            {workspace.canManageHomework ? <DeleteHomeworkDialog
              homeworkName={deleteTarget?.homeworkName}
              isOpen={Boolean(deleteTarget)}
              isDeleting={Boolean(
                deleteTarget &&
                  mutation.state.status === "pending" &&
                  mutation.state.action === `delete:${deleteTarget.id}`,
              )}
              hasError={Boolean(
                deleteTarget &&
                  mutation.state.status === "error" &&
                  mutation.state.action === `delete:${deleteTarget.id}`,
              )}
              onCancel={() => {
                setDeleteTarget(null);
                mutation.reset();
              }}
              onConfirm={deleteHomework}
            /> : null}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
